import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { menuInfo } from "@shared/type/CommonType";
import { fnGetDataSet, callApi } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { showResizablePopupModal } from "@widgets/CommonModal";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import WbsGantt, { BarKind } from "./WbsGantt";
import ProjectList from "./ProjectList";
import SN_SFA_PJT_REG_002_POP_001 from "./SN_SFA_PJT_REG_002_POP_001";
import SN_SFA_PJT_REG_002_POP_002 from "./SN_SFA_PJT_REG_002_POP_002";
import { Act, Project } from "./wbsCalc";

/**
 * SN_SFA_PJT_REG_002 — 프로젝트일정등록 (그룹웨어, ISS2026100053)
 * 조회조건(프로젝트/상태/사용중만)은 sys_formctrl(cd_form='search') 메타 구동 SearchPanel.
 *  - 프로젝트/상태는 로드한 데이터의 클라이언트 필터(간트 트리 유지), 사용중만은 서버 재조회(yn_use)
 *  - 데이터: sys_query 'sn_sfa_pjt_reg_002_01'
 */
interface Props {
  Menuprops: menuInfo;
}

// flat 행 → 프로젝트별 2뎁스(대표ACT > 세부ACT) 트리
function buildProjects(rows: any[]): Project[] {
  const order: string[] = [];
  const byPjt = new Map<string, any[]>();
  rows.forEach((r) => {
    if (!byPjt.has(r.cd_pjt)) {
      byPjt.set(r.cd_pjt, []);
      order.push(r.cd_pjt);
    }
    byPjt.get(r.cd_pjt)!.push(r);
  });

  return order.map((cd_pjt) => {
    const prows = byPjt.get(cd_pjt)!;
    const levelSet = new Set(prows.map((r) => String(r.cd_level)));
    const idByLevel = new Map<string, string>();
    const actOf = (r: any): Act => ({
      id: `${cd_pjt}#${r.no_line}`,
      name: r.nm_activity || r.cd_activity || "",
      ps: r.dt_start || "",
      pe: r.dt_end || "",
      as: r.dt_work_start || undefined,
      ae: r.dt_work_end || undefined,
      weight: Number(r.rt_weight) || 0,
    });

    const acts = new Map<string, Act>();
    prows.forEach((r) => {
      const a = actOf(r);
      acts.set(a.id, a);
      idByLevel.set(String(r.cd_level), a.id);
    });

    const roots: Act[] = [];
    prows.forEach((r) => {
      const a = acts.get(`${cd_pjt}#${r.no_line}`)!;
      const plevel = String(r.cd_plevel ?? "");
      const parentId = plevel && levelSet.has(plevel) ? idByLevel.get(plevel) : undefined;
      if (parentId && parentId !== a.id) {
        const parent = acts.get(parentId)!;
        (parent.children ||= []).push(a);
      } else {
        roots.push(a);
      }
    });

    // 대표ACT 일자 = 세부ACT 기간(최소 시작 ~ 최대 종료). 실적종료는 세부ACT 가 모두 끝났을 때만.
    roots.forEach((p) => {
      const kids = p.children;
      if (!kids?.length) return;
      const min = (v: (string | undefined)[]) => v.filter(Boolean).sort()[0] as string | undefined;
      const max = (v: (string | undefined)[]) => v.filter(Boolean).sort().pop() as string | undefined;
      p.ps = min(kids.map((k) => k.ps)) || "";
      p.pe = max(kids.map((k) => k.pe)) || "";
      p.as = min(kids.map((k) => k.as));
      p.ae = kids.every((k) => k.ae) ? max(kids.map((k) => k.ae)) : undefined;
    });

    return {
      id: cd_pjt,
      name: prows[0].nm_pjt || cd_pjt,
      pm: prows[0].nm_pm || "",
      acts: roots,
    };
  });
}

const SN_SFA_PJT_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const searchRef = useRef<any>(null);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [headerInfo, setHeaderInfo] = useState<any>({ controls: [], data: {} });
  const [projects, setProjects] = useState<Project[]>([]);
  const [fProject, setFProject] = useState(""); // 프로젝트 필터(cd_pjt) — 클라이언트
  const [fStatus, setFStatus] = useState(""); // 상태 필터(계산값) — 클라이언트
  const [selectedPjt, setSelectedPjt] = useState(""); // 간트에서 클릭 선택한 프로젝트(WBS 등록 대상)

  useEffect(() => {
    (async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setHeaderInfo(rtn.header?.grid1 ?? { controls: [], data: {} });
        setFProject(rtn.formData?.cd_pjt || "");
        setFStatus(rtn.formData?.st_status || "");
        await search(rtn.formData);
      } catch (e) {
        await search({ yn_use: "Y" });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 사용중만(yn_use)만 서버 재조회. 프로젝트/상태는 로드 데이터의 클라이언트 필터.
  const search = async (override?: any) => {
    try {
      const f = override || searchRef.current?.getFormdata() || {};
      // 사용여부: SnCheckBox 는 'Y'/'N' 방출 → 그대로 전달(기본 Y). '' 로 뭉개지 않는다.
      const yn = f.yn_use === "N" ? "N" : "Y";
      // 조회 시 프로젝트/상태 클라이언트 필터도 현재 조회조건 값으로 동기화
      setFProject(f.cd_pjt || "");
      setFStatus(f.st_status || "");
      const data: any = await fnGetDataSet({
        id: "sn_sfa_pjt_reg_002_01",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: "", // 사업장 구분 없이 회사 전체 (조회조건 프로젝트 콤보와 동일 범위)
          cd_pjt: "",
          yn_use: yn,
        },
      });
      setProjects(buildProjects(data?.[0] || []));
    } catch (error) {
      setProjects([]);
    }
  };

  const onSearchValueChanged = (event: ValueChangedEvent) => {
    if (event.colId === "cd_pjt") setFProject(event.newValue ?? "");
    else if (event.colId === "st_status") setFStatus(event.newValue ?? "");
    else if (event.colId === "yn_use") {
      // 체크 토글 즉시 재조회 — getFormdata 가 아직 반영 전일 수 있어 newValue 를 직접 넘긴다
      const cur = searchRef.current?.getFormdata() || {};
      search({ ...cur, yn_use: event.newValue ?? cur.yn_use });
    }
  };

  useImperativeHandle(ref, () => ({
    onSearch: () => search(),
  }));

  // 간트 계획·실적막대 이동 → 해당 일자 저장 후 재조회 (실적 진행 중이면 pe 는 "")
  const handleMove = async (cdPjt: string, noLine: string, ps: string, pe: string, bar: BarKind) => {
    try {
      await callApi("SN_SFA_PJT_REG_002_WBS_MOVE_01", {
        cd_firm: userInfo.cd_firm,
        cd_pjt: cdPjt,
        no_line: noLine,
        dt_start: ps,
        dt_end: pe,
        fg_bar: bar,
      });
      search();
    } catch (error) {
      search();
    }
  };

  // 프로젝트 등록 팝업
  // 프로젝트 등록 팝업
  const openProjectReg = async () => {
    await showResizablePopupModal({
      title: "프로젝트 등록",
      children: <SN_SFA_PJT_REG_002_POP_001 />,
      width: "720px",
      height: "560px",
      validation: true,
      okayBtnText: "등록",
    });
    search();
  };

  // 프로젝트 수정 팝업 (간트 프로젝트 행 더블클릭) — 등록 팝업 재사용, 삭제는 사용여부 해제(사용안함)
  const editOpenRef = useRef(false); // 더블클릭 연속 입력으로 팝업이 겹쳐 열리지 않게
  const openProjectEdit = async (cdPjt: string) => {
    if (!cdPjt || editOpenRef.current) return;
    editOpenRef.current = true;
    setSelectedPjt(cdPjt);
    try {
      await showResizablePopupModal({
        title: "프로젝트 수정",
        children: <SN_SFA_PJT_REG_002_POP_001 editPjt={cdPjt} />,
        width: "720px",
        height: "560px",
        validation: true,
        okayBtnText: "저장",
      });
    } finally {
      editOpenRef.current = false;
    }
    search();
  };

  // WBS 진행등록 팝업 (메타 구동: sys_popupctrl/sys_popupgridcolumn)
  const openWbsReg = async () => {
    await showResizablePopupModal({
      title: "WBS 진행등록",
      children: <SN_SFA_PJT_REG_002_POP_002 selectedData={{ cd_pjt: selectedPjt || fProject || "" }} />,
      width: "1000px",
      height: "880px",
      validation: true,
      okayBtnText: "저장",
    });
    search();
  };

  // 헤더 버튼(sys_headerctrl) 클릭 → cd_param 분기
  const onHeaderBtnClick = (id: string) => {
    if (id === "btnProjectReg") openProjectReg();
    else if (id === "btnWbsReg") openWbsReg();
  };

  return (
    <div className="h-full flex flex-col">
      {/* 조회조건 (sys_formctrl) */}
      <SearchPanel
        ref={searchRef}
        data={formData}
        controls={formControlData}
        onValueChanged={onSearchValueChanged}
      />
      <div className="flex-1 min-h-0 mt-1">
        <SnTab
          titles={["WBS현황"]}
          // 동작 버튼(sys_headerctrl)을 탭 라인 우측에 배치
          header={[
            <SnTabHeader key="h1" headerInfo={headerInfo} onBtnEtcClick={onHeaderBtnClick} />,
          ]}
          body={[
            <WbsGantt projects={projects} filterProjectId={fProject || undefined} filterStatus={fStatus || undefined} selectedProjectId={selectedPjt || undefined} onSelectProject={setSelectedPjt} onEditProject={openProjectEdit} onMove={handleMove} />,
            // 프로젝트목록 탭은 현재 보류(숨김). 되살리려면 titles/header/body 에 다시 추가:
            // "프로젝트목록" / <SnTabHeader key="h2" .../> / <ProjectList projects={projects} filterProjectId={fProject||undefined} filterStatus={fStatus||undefined} />
          ]}
        />
      </div>
    </div>
  );
});

export default SN_SFA_PJT_REG_002;
